import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, HandCoins, AlertTriangle, MessageCircle, FlaskConical, Search } from "lucide-react";
import { openWhatsApp, lowStockMsg, MANAGER_WHATSAPP } from "../../lib/whatsapp";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, PageHeader } from "./shared";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
const todayStr = () => new Date().toISOString().split("T")[0];
const PRODUCT_CATEGORIES = ["General", "Hair Care", "Skin", "Waxing", "Chemical", "Facial Kits", "Nails", "Makeup", "Tools"];
const emptyProduct = { name: "", brand: "", category: "General", product_type: "Retail", purchase_price: "", selling_price: "", mrp: "", stock: "", low_stock_level: 2, unit: "pcs", batch_no: "", expiry: "", barcode: "", pack_size: "" };
const emptySupplier = { name: "", phone: "", address: "", notes: "", opening_balance: "", balance_type: "to_pay", gstin: "" };
const emptyUsage = () => ({ product_id: "", qty: "", staff_id: "", note: "", date: todayStr(), usage_unit: "pcs", pack_size: "" });
const emptyBuy = { supplier_id: "", amount_paid: "", payment_method: "Cash", notes: "", invoice_type: "simple", gst_percent: "", last_balance: "", credit_card_id: "" };

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [cardsList, setCardsList] = useState([]);
  const [pOpen, setPOpen] = useState(false);
  const [pEdit, setPEdit] = useState(null);
  const [pForm, setPForm] = useState(emptyProduct);
  const [sOpen, setSOpen] = useState(false);
  const [sEdit, setSEdit] = useState(null);
  const [sForm, setSForm] = useState(emptySupplier);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyForm, setBuyForm] = useState(emptyBuy);
  const [buyItems, setBuyItems] = useState([]);
  const [paying, setPaying] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", method: "Cash", credit_card_id: "" });
  const [usage, setUsage] = useState({ rows: [], total_cost: 0 });
  const [staffList, setStaffList] = useState([]);
  const [uOpen, setUOpen] = useState(false);
  const [uEdit, setUEdit] = useState(null);
  const [uForm, setUForm] = useState(emptyUsage());
  const [search, setSearch] = useState("");
  const [chip, setChip] = useState("all");
  const [activeRow, setActiveRow] = useState(-1);
  const [uQuery, setUQuery] = useState("");
  const [uIdx, setUIdx] = useState(0);

  const load = useCallback(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/suppliers").then(({ data }) => setSuppliers(data)).catch(() => {});
    api.get("/purchases").then(({ data }) => setPurchases(data)).catch(() => {});
    api.get("/product-usage").then(({ data }) => setUsage(data)).catch(() => {});
    api.get("/staff").then(({ data }) => setStaffList(data)).catch(() => {});
    api.get("/credit-cards").then(({ data }) => setCardsList(data.rows || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

  const q = search.trim().toLowerCase();
  const filteredProducts = products.filter((p) => {
    if (chip === "retail" && p.product_type !== "Retail") return false;
    if (chip === "backbar" && p.product_type !== "Backbar") return false;
    if (chip === "low" && !(p.stock <= p.low_stock_level)) return false;
    if (!q) return true;
    return (p.name + " " + (p.brand || "")).toLowerCase().includes(q);
  });

  useEffect(() => {
    const anyDialog = pOpen || sOpen || buyOpen || uOpen || !!paying;
    const h = (e) => {
      if (anyDialog) return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (["input", "select", "textarea"].includes(tag)) return;
      if (e.key !== "PageDown" && e.key !== "PageUp") return;
      e.preventDefault();
      setActiveRow((i) => {
        const n = filteredProducts.length;
        if (!n) return -1;
        const next = e.key === "PageDown" ? Math.min((i < 0 ? -1 : i) + 5, n - 1) : Math.max((i < 0 ? 0 : i) - 5, 0);
        document.getElementById(`product-tr-${next}`)?.scrollIntoView({ block: "nearest" });
        return next;
      });
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [filteredProducts.length, pOpen, sOpen, buyOpen, uOpen, paying]);

  const saveUsage = async (e) => {
    e.preventDefault();
    if (!uForm.product_id || !Number(uForm.qty)) { toast.error("Pick a product and quantity"); return; }
    if (uForm.usage_unit === "g" && !(Number(uForm.pack_size) > 0)) { toast.error("Enter the container size (g/ml per pc) for proportional deduction"); return; }
    const payload = { product_id: uForm.product_id, qty: Number(uForm.qty), staff_id: uForm.staff_id, note: uForm.note, date: uForm.date, usage_unit: uForm.usage_unit, pack_size: Number(uForm.pack_size) || 0 };
    try {
      if (uEdit) {
        await api.put(`/product-usage/${uEdit.id}`, payload);
        toast.success("Usage entry updated — stock adjusted");
      } else {
        const { data } = await api.post("/product-usage", payload);
        toast.success(`Logged — ${data.stock_left} ${data.unit} left in stock`);
      }
      setUOpen(false); setUEdit(null); setUForm(emptyUsage()); setUQuery(""); load();
    } catch (e2) { err(e2); }
  };

  const uProduct = products.find((p) => p.id === uForm.product_id);
  const uMatches = uQuery.trim()
    ? products.filter((p) => (p.name + " " + (p.brand || "")).toLowerCase().includes(uQuery.trim().toLowerCase())).slice(0, 30)
    : [];

  const pickUsageProduct = (p) => {
    if (!p) return;
    if ((p.stock ?? 0) <= 0) { toast.error("Out of stock — restock before recording usage"); return; }
    setUForm({ ...uForm, product_id: p.id, pack_size: p.pack_size ? String(p.pack_size) : uForm.pack_size });
    setUQuery("");
    setUIdx(0);
  };

  const usageKeyNav = (e) => {
    const n = uMatches.length;
    if (!n || !["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Enter"].includes(e.key)) return;
    e.preventDefault();
    if (e.key === "Enter") { pickUsageProduct(uMatches[Math.min(uIdx, n - 1)]); return; }
    let next = uIdx;
    if (e.key === "ArrowDown") next = Math.min(uIdx + 1, n - 1);
    if (e.key === "ArrowUp") next = Math.max(uIdx - 1, 0);
    if (e.key === "PageDown") next = Math.min(uIdx + 5, n - 1);
    if (e.key === "PageUp") next = Math.max(uIdx - 5, 0);
    setUIdx(next);
    setTimeout(() => document.getElementById(`usage-opt-${next}`)?.scrollIntoView({ block: "nearest" }), 0);
  };

  const delUsage = async (id) => {
    try {
      await api.delete(`/product-usage/${id}`);
      toast.success("Entry deleted — stock restored");
      load();
    } catch (e2) { err(e2); }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!pForm.name || !pForm.selling_price) { toast.error("Name and selling price required"); return; }
    const payload = {
      ...pForm,
      purchase_price: Number(pForm.purchase_price) || 0,
      selling_price: Number(pForm.selling_price),
      mrp: Number(pForm.mrp) || 0,
      stock: Number(pForm.stock) || 0,
      low_stock_level: Number(pForm.low_stock_level) || 0,
      pack_size: Number(pForm.pack_size) || 0,
    };
    try {
      if (pEdit) await api.put(`/products/${pEdit.id}`, payload);
      else await api.post("/products", payload);
      toast.success(pEdit ? "Product updated" : "Product added");
      setPOpen(false); load();
    } catch (e2) { err(e2); }
  };

  const saveSupplier = async (e) => {
    e.preventDefault();
    if (!sForm.name) { toast.error("Supplier name required"); return; }
    const payload = { ...sForm, opening_balance: Number(sForm.opening_balance) || 0 };
    try {
      if (sEdit) await api.put(`/suppliers/${sEdit.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(sEdit ? "Supplier updated" : "Supplier added");
      setSOpen(false); load();
    } catch (e2) { err(e2); }
  };

  const supplierReorderMsg = (s) => {
    const low = products.filter((p) => p.stock <= p.low_stock_level);
    const lines = low.slice(0, 15).map((p, i) => `${i + 1}. ${p.name}${p.brand ? ` (${p.brand})` : ""} — order ${Math.max(Math.round(((p.low_stock_level || 2) * 2 - p.stock) * 100) / 100, 1)} ${p.unit}`);
    return `Namaste ${s.name} 🙏\nGREEN Professionals Salon, Sonipat se stock order:\n\n${lines.length ? lines.join("\n") : "Hume kuch products reorder karne hain — list bhej rahe hain."}\n\nKripya availability aur rate confirm karein. Dhanyavaad!`;
  };

  const pickBuySupplier = (id) => {
    const s = suppliers.find((x) => x.id === id);
    let lb = "";
    if (s && (s.balance_type || "to_pay") === "to_pay" && (s.opening_balance || 0) > 0) {
      const applied = purchases.filter((p) => p.supplier_id === id).reduce((sum, p) => sum + (p.last_balance || 0), 0);
      const remaining = Math.max(0, Math.round(((s.opening_balance || 0) - applied) * 100) / 100);
      if (remaining > 0) lb = String(remaining);
    }
    setBuyForm({ ...buyForm, supplier_id: id, last_balance: lb });
  };
  const buySupplier = suppliers.find((x) => x.id === buyForm.supplier_id);

  const addBuyItem = (e) => {
    const pr = products.find((x) => x.id === e.target.value);
    if (pr) setBuyItems([...buyItems, { uid: crypto.randomUUID(), product_id: pr.id, name: pr.name, qty: 1, cost: pr.purchase_price || 0, free_qty: "", discount_percent: "", batch_no: "", expiry: "" }]);
    e.target.value = "";
  };
  const setBuyItem = (uid, k, v) => setBuyItems(buyItems.map((it) => (it.uid === uid ? { ...it, [k]: v } : it)));
  const lineTotal = (it) => (Number(it.qty) || 0) * (Number(it.cost) || 0) * (1 - (Number(it.discount_percent) || 0) / 100);
  const buySubtotal = buyItems.reduce((s, it) => s + lineTotal(it), 0);
  const buyGst = buyForm.invoice_type === "gst" ? (buySubtotal * (Number(buyForm.gst_percent) || 0)) / 100 : 0;
  const buyTotal = buySubtotal + buyGst;
  const buyLB = Number(buyForm.last_balance) || 0;
  const buyOutstanding = buyTotal + buyLB;
  const buyPending = Math.max(0, buyOutstanding - (Number(buyForm.amount_paid) || 0));

  const savePurchase = async (e) => {
    e.preventDefault();
    if (!buyForm.supplier_id) { toast.error("Select a supplier"); return; }
    if (buyItems.length === 0) { toast.error("Add at least one item"); return; }
    try {
      await api.post("/purchases", {
        ...buyForm,
        amount_paid: Number(buyForm.amount_paid) || 0,
        gst_percent: Number(buyForm.gst_percent) || 0,
        last_balance: Number(buyForm.last_balance) || 0,
        items: buyItems.map(({ uid, ...r }) => ({
          ...r, qty: Number(r.qty) || 1, cost: Number(r.cost) || 0,
          free_qty: Number(r.free_qty) || 0, discount_percent: Number(r.discount_percent) || 0,
        })),
      });
      toast.success("Purchase recorded — stock updated");
      setBuyOpen(false); setBuyItems([]); setBuyForm(emptyBuy);
      load();
    } catch (e2) { err(e2); }
  };

  const payPurchase = async (e) => {
    e.preventDefault();
    const amt = Number(payForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      const { data } = await api.post(`/purchases/${paying.id}/payment`, { amount: amt, method: payForm.method, credit_card_id: payForm.credit_card_id });
      toast.success(data.paid ? "Supplier fully paid" : `Paid ${fmt(amt)} — ${fmt(data.balance_due)} still due`);
      setPaying(null); setPayForm({ amount: "", method: "Cash", credit_card_id: "" });
      load();
    } catch (e2) { err(e2); }
  };

  const del = (path) => async (id) => {
    try { await api.delete(`/${path}/${id}`); toast.success("Deleted"); load(); } catch (e2) { err(e2); }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <PageHeader title="Inventory" subtitle="Products, stock, suppliers and purchase payments." />
      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="tab-purchases">Stock Purchases</TabsTrigger>
          <TabsTrigger value="usage" data-testid="tab-usage">Salon Use</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => { setPEdit(null); setPForm(emptyProduct); setPOpen(true); }} data-testid="add-product-btn"
              className="btn-lift bg-[#0A3F2F] text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-[#0d5540]">
              <Plus className="w-4 h-4" /> Add Product
            </button>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6A63]" />
              <input data-testid="product-search-input" className={`${inputCls} !pl-9 max-w-xs`} placeholder="Search by name or brand…"
                value={search} onChange={(e) => { setSearch(e.target.value); setActiveRow(-1); }} />
            </div>
            {[["all", "All"], ["retail", "Retail"], ["backbar", "Backbar"], ["low", "Low Stock (Reorder)"]].map(([k, label]) => (
              <button key={k} onClick={() => { setChip(k); setActiveRow(-1); }} data-testid={`product-chip-${k}`}
                className={`text-xs font-semibold px-3.5 py-2 rounded-full ${chip === k ? "bg-[#C5A059] text-[#1C1C1C]" : "bg-white border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F]"}`}>
                {label}
              </button>
            ))}
            <span className="text-[11px] text-[#5C6A63]">PgUp / PgDn to jump the list</span>
          </div>
          <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto max-h-[65vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                  <th className="px-4 py-3 font-semibold">Product</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Stock</th>
                  <th className="px-4 py-3 font-semibold">Batch</th>
                  <th className="px-4 py-3 font-semibold">Expiry</th>
                  <th className="px-4 py-3 font-semibold">MRP</th>
                  <th className="px-4 py-3 font-semibold">Cost</th>
                  <th className="px-4 py-3 font-semibold">Selling</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="products-table-body">
                {filteredProducts.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-[#5C6A63]" data-testid="no-products">No products found.</td></tr>}
                {filteredProducts.map((p, idx) => (
                  <tr key={p.id} id={`product-tr-${idx}`} className={`border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8] ${activeRow === idx ? "bg-[#C5A059]/10" : ""}`} data-testid={`product-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-[#1C1C1C]">{p.name}</p>
                      <p className="text-xs text-[#5C6A63]">{p.brand}{p.brand && " · "}{p.category}{p.barcode ? ` · ${p.barcode}` : ""}</p>
                    </td>
                    <td className="px-4 py-3"><span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${p.product_type === "Retail" ? "bg-[#0A3F2F]/10 text-[#0A3F2F]" : "bg-[#C5A059]/20 text-[#8a6d33]"}`}>{p.product_type}</span></td>
                    <td className="px-4 py-3">
                      <span className={`font-semibold ${p.stock <= p.low_stock_level ? "text-red-600" : "text-[#1C1C1C]"}`} data-testid={`stock-${p.id}`}>
                        {p.stock} {p.unit}
                      </span>
                      {p.stock <= p.low_stock_level && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-red-600" data-testid={`low-stock-${p.id}`}>
                          <AlertTriangle className="w-3 h-3" /> LOW
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#5C6A63]">{p.batch_no || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#5C6A63]">{p.expiry || "—"}</td>
                    <td className="px-4 py-3 text-xs">{p.mrp ? fmt(p.mrp) : "—"}</td>
                    <td className="px-4 py-3">{fmt(p.purchase_price)}</td>
                    <td className="px-4 py-3 font-semibold text-[#0A3F2F]">{fmt(p.selling_price)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3">
                        {p.stock <= p.low_stock_level && (
                          <button onClick={() => openWhatsApp(MANAGER_WHATSAPP, lowStockMsg(p))} data-testid={`alert-manager-${p.id}`} title="Alert manager on WhatsApp"
                            className="text-red-500 hover:text-emerald-600"><MessageCircle className="w-4 h-4" /></button>
                        )}
                        <button onClick={() => { setPEdit(p); setPForm({ ...emptyProduct, ...p }); setPOpen(true); }} data-testid={`edit-product-${p.id}`} className="text-[#3D4A44] hover:text-[#0A3F2F]"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => del("products")(p.id)} data-testid={`delete-product-${p.id}`} className="text-[#3D4A44] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-4 mt-4">
          <button onClick={() => { setSEdit(null); setSForm(emptySupplier); setSOpen(true); }} data-testid="add-supplier-btn"
            className="btn-lift bg-[#0A3F2F] text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-[#0d5540]">
            <Plus className="w-4 h-4" /> Add Supplier
          </button>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="suppliers-grid">
            {suppliers.length === 0 && <p className="text-sm text-[#5C6A63]" data-testid="no-suppliers">No suppliers yet.</p>}
            {suppliers.map((s) => (
              <div key={s.id} className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm" data-testid={`supplier-card-${s.id}`}>
                <div className="flex items-start justify-between">
                  <h3 className="font-bold text-[#1C1C1C]">{s.name}</h3>
                  <div className="flex gap-2">
                    <button onClick={() => { setSEdit(s); setSForm({ name: s.name, phone: s.phone || "", address: s.address || "", notes: s.notes || "", opening_balance: s.opening_balance ? String(s.opening_balance) : "", balance_type: s.balance_type || "to_pay", gstin: s.gstin || "" }); setSOpen(true); }} data-testid={`edit-supplier-${s.id}`} className="text-[#3D4A44] hover:text-[#0A3F2F]"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del("suppliers")(s.id)} data-testid={`delete-supplier-${s.id}`} className="text-[#3D4A44] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <p className="text-sm text-[#5C6A63] mt-2 flex items-center gap-2">
                  {s.phone || "No phone"}
                  {s.phone && (
                    <button onClick={() => openWhatsApp(s.phone, supplierReorderMsg(s))} data-testid={`supplier-wa-${s.id}`} title="Send stock order on WhatsApp"
                      className="text-emerald-600 hover:text-emerald-700"><MessageCircle className="w-4 h-4" /></button>
                  )}
                </p>
                <p className="text-xs text-[#5C6A63] mt-1">{s.address}</p>
                {s.gstin && <p className="text-xs text-[#5C6A63] mt-1">GSTIN: {s.gstin}</p>}
                {(s.opening_balance || 0) > 0 && (
                  <span className={`inline-block mt-2 text-[10px] font-bold px-2.5 py-1 rounded-full ${(s.balance_type || "to_pay") === "to_pay" ? "bg-red-100 text-red-600" : "bg-emerald-600/10 text-emerald-700"}`} data-testid={`supplier-opening-${s.id}`}>
                    {(s.balance_type || "to_pay") === "to_pay" ? "Opening dena hai" : "Advance jama hai"}: {fmt(s.opening_balance)}
                  </span>
                )}
                {s.notes && <p className="text-xs text-[#8a6d33] mt-2 italic">{s.notes}</p>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="purchases" className="space-y-4 mt-4">
          <button onClick={() => setBuyOpen(true)} data-testid="add-purchase-btn"
            className="btn-lift bg-[#0A3F2F] text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-[#0d5540]">
            <Plus className="w-4 h-4" /> New Purchase
          </button>
          <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Supplier</th>
                  <th className="px-4 py-3 font-semibold">Items</th>
                  <th className="px-4 py-3 font-semibold">Total</th>
                  <th className="px-4 py-3 font-semibold">Paid</th>
                  <th className="px-4 py-3 font-semibold">Balance</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="purchases-table-body">
                {purchases.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#5C6A63]" data-testid="no-purchases">No purchases yet.</td></tr>}
                {purchases.map((p) => (
                  <tr key={p.id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`purchase-row-${p.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {p.date}
                      <p className="text-[10px] text-[#5C6A63] uppercase font-bold">{p.invoice_type === "gst" ? `Tax Invoice ${p.gst_percent}%` : "Estimate"}</p>
                    </td>
                    <td className="px-4 py-3 font-semibold text-[#1C1C1C]">{p.supplier_name}</td>
                    <td className="px-4 py-3 text-[#5C6A63]">{p.items.map((it) => `${it.name} ×${it.qty}${it.free_qty ? `+${it.free_qty} free` : ""}`).join(", ")}</td>
                    <td className="px-4 py-3 font-semibold">
                      {fmt(p.grand_total ?? p.total)}
                      {p.last_balance > 0 && <p className="text-[10px] text-[#8a6d33]">incl. L.B. {fmt(p.last_balance)}</p>}
                    </td>
                    <td className="px-4 py-3">
                      {fmt(p.amount_paid)}
                      <p className="text-[10px] text-[#5C6A63]">{p.payments.map((pm) => `${fmt(pm.amount)} ${pm.method} (${pm.date})`).join(", ")}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${p.paid ? "bg-emerald-600/10 text-emerald-700" : "bg-red-100 text-red-600"}`} data-testid={`purchase-balance-${p.id}`}>
                        {p.paid ? "Settled" : `Owe ${fmt(p.balance_due)}`}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3">
                        {!p.paid && (
                          <button onClick={() => { setPaying(p); setPayForm({ amount: String(p.balance_due), method: "Cash", credit_card_id: "" }); }} data-testid={`pay-purchase-${p.id}`} title="Pay supplier"
                            className="text-[#C5A059] hover:text-[#0A3F2F]"><HandCoins className="w-4 h-4" /></button>
                        )}
                        <button onClick={() => del("purchases")(p.id)} data-testid={`delete-purchase-${p.id}`} className="text-[#3D4A44] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="usage" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button onClick={() => { setUEdit(null); setUForm(emptyUsage()); setUOpen(true); }} data-testid="record-usage-btn"
              className="btn-lift bg-[#0A3F2F] text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-[#0d5540]">
              <FlaskConical className="w-4 h-4" /> Record Salon Use
            </button>
            <span className="text-sm font-semibold text-[#8a6d33]" data-testid="usage-total-cost">Total consumption value: {fmt(usage.total_cost)}</span>
          </div>
          <p className="text-xs text-[#5C6A63]">Products your team uses in-house (colour tubes, shampoo, wax…). Stock is deducted automatically and restored if an entry is deleted. Staff can also log usage from their My Day screen.</p>
          <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Product</th>
                  <th className="px-4 py-3 font-semibold">Qty</th>
                  <th className="px-4 py-3 font-semibold">Used By</th>
                  <th className="px-4 py-3 font-semibold">Note</th>
                  <th className="px-4 py-3 font-semibold">Cost</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="usage-table-body">
                {usage.rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#5C6A63]" data-testid="no-usage">No salon usage logged yet.</td></tr>}
                {usage.rows.map((u) => (
                  <tr key={u.id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`usage-row-${u.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">{u.date}</td>
                    <td className="px-4 py-3 font-semibold text-[#1C1C1C]">{u.product_name}</td>
                    <td className="px-4 py-3">
                      {u.usage_unit === "g" ? `${u.qty} g/ml` : `${u.qty} ${u.unit}`}
                      {u.usage_unit === "g" && <p className="text-[10px] text-[#5C6A63]">= {u.stock_qty} {u.unit}</p>}
                    </td>
                    <td className="px-4 py-3 text-[#5C6A63]">{u.staff_name || "—"}</td>
                    <td className="px-4 py-3 text-[#5C6A63]">{u.note || "—"}</td>
                    <td className="px-4 py-3 font-semibold text-[#8a6d33]">{fmt(u.total_cost)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3">
                        <button onClick={() => { setUEdit(u); setUForm({ product_id: u.product_id, qty: String(u.qty), staff_id: u.staff_id || "", note: u.note || "", date: u.date, usage_unit: u.usage_unit || "pcs", pack_size: u.pack_size ? String(u.pack_size) : "" }); setUOpen(true); }}
                          data-testid={`edit-usage-${u.id}`} className="text-[#3D4A44] hover:text-[#0A3F2F]"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => delUsage(u.id)} data-testid={`delete-usage-${u.id}`} className="text-[#3D4A44] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{pEdit ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <form onSubmit={saveProduct} className="space-y-3" data-testid="product-form">
            <input data-testid="product-name-input" className={inputCls} placeholder="Product name *" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="product-brand-input" className={inputCls} placeholder="Brand (e.g., L'Oréal)" value={pForm.brand} onChange={(e) => setPForm({ ...pForm, brand: e.target.value })} />
              <select data-testid="product-category-select" className={inputCls} value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })}>
                {!PRODUCT_CATEGORIES.includes(pForm.category) && pForm.category && <option>{pForm.category}</option>}
                {PRODUCT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <select data-testid="product-type-select" className={inputCls} value={pForm.product_type} onChange={(e) => setPForm({ ...pForm, product_type: e.target.value })}>
              <option value="Retail">Retail (for sale)</option>
              <option value="Backbar">Backbar (internal use)</option>
            </select>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C6A63] font-semibold">Printed MRP ₹</label>
                <input data-testid="product-mrp-input" type="number" step="any" className={inputCls} placeholder="MRP" value={pForm.mrp} onChange={(e) => setPForm({ ...pForm, mrp: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C6A63] font-semibold">Cost price ₹</label>
                <input data-testid="product-cost-input" type="number" step="any" className={inputCls} placeholder="Cost" value={pForm.purchase_price} onChange={(e) => setPForm({ ...pForm, purchase_price: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C6A63] font-semibold">Selling ₹ *</label>
                <input data-testid="product-price-input" type="number" step="any" className={inputCls} placeholder="Selling" value={pForm.selling_price} onChange={(e) => setPForm({ ...pForm, selling_price: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C6A63] font-semibold">Opening stock</label>
                <input data-testid="product-stock-input" type="number" step="any" className={inputCls} placeholder="Stock" value={pForm.stock} onChange={(e) => setPForm({ ...pForm, stock: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C6A63] font-semibold">Low stock alert</label>
                <input data-testid="product-lowstock-input" type="number" step="any" className={inputCls} placeholder="2" value={pForm.low_stock_level} onChange={(e) => setPForm({ ...pForm, low_stock_level: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C6A63] font-semibold">Unit</label>
                <input data-testid="product-unit-input" className={inputCls} placeholder="pcs" value={pForm.unit} onChange={(e) => setPForm({ ...pForm, unit: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C6A63] font-semibold">Batch no.</label>
                <input data-testid="product-batch-input" className={inputCls} placeholder="Batch" value={pForm.batch_no} onChange={(e) => setPForm({ ...pForm, batch_no: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C6A63] font-semibold">Expiry (MM/YYYY)</label>
                <input data-testid="product-expiry-input" type="month" className={inputCls} value={pForm.expiry} onChange={(e) => setPForm({ ...pForm, expiry: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C6A63] font-semibold">Barcode / SKU</label>
                <input data-testid="product-barcode-input" className={inputCls} placeholder="SKU" value={pForm.barcode} onChange={(e) => setPForm({ ...pForm, barcode: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#5C6A63] font-semibold">Container size — g/ml per {pForm.unit || "pc"} (optional, for salon-use in grams)</label>
              <input data-testid="product-packsize-input" type="number" step="any" min="0" className={inputCls} placeholder="e.g. 60 for a 60g colour tube" value={pForm.pack_size} onChange={(e) => setPForm({ ...pForm, pack_size: e.target.value })} />
            </div>
            <button type="submit" data-testid="product-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              {pEdit ? "Save Changes" : "Add Product"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={sOpen} onOpenChange={setSOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{sEdit ? "Edit Supplier" : "Add Supplier"}</DialogTitle></DialogHeader>
          <form onSubmit={saveSupplier} className="space-y-3" data-testid="supplier-form">
            <input data-testid="supplier-name-input" className={inputCls} placeholder="Supplier / counter name *" value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} />
            <input data-testid="supplier-phone-input" className={inputCls} placeholder="Phone" value={sForm.phone} onChange={(e) => setSForm({ ...sForm, phone: e.target.value })} />
            <input data-testid="supplier-address-input" className={inputCls} placeholder="Address / market" value={sForm.address} onChange={(e) => setSForm({ ...sForm, address: e.target.value })} />
            <input data-testid="supplier-gstin-input" className={inputCls} placeholder="GSTIN number (optional — for tax invoices)" value={sForm.gstin} onChange={(e) => setSForm({ ...sForm, gstin: e.target.value.toUpperCase() })} />
            <div className="bg-[#FAFAF8] border border-[#E5E7E5] rounded-md p-3 space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Opening outstanding balance (₹)</label>
              <input data-testid="supplier-opening-input" type="number" min="0" step="any" className={inputCls} placeholder="0" value={sForm.opening_balance} onChange={(e) => setSForm({ ...sForm, opening_balance: e.target.value })} />
              <div className="flex gap-2" data-testid="supplier-balance-type-toggle">
                {[["to_pay", "To Pay (Dena hai)"], ["advance", "Advance Paid (Jama hai)"]].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setSForm({ ...sForm, balance_type: k })} data-testid={`supplier-balance-${k}`}
                    className={`flex-1 text-[11px] font-bold px-3 py-2 rounded-md border ${sForm.balance_type === k ? (k === "to_pay" ? "bg-red-600 text-white border-red-600" : "bg-emerald-600 text-white border-emerald-600") : "bg-white border-[#E5E7E5] text-[#5C6A63]"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[#5C6A63]">“To Pay” balance auto-fills as Last Balance (L.B.) in the next Stock Purchase for this supplier.</p>
            </div>
            <textarea data-testid="supplier-notes-input" className={inputCls} rows={2} placeholder="Notes" value={sForm.notes} onChange={(e) => setSForm({ ...sForm, notes: e.target.value })} />
            <button type="submit" data-testid="supplier-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              {sEdit ? "Save Changes" : "Add Supplier"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Stock Purchase</DialogTitle></DialogHeader>
          <form onSubmit={savePurchase} className="space-y-4" data-testid="purchase-form">
            <div className="flex gap-2" data-testid="invoice-type-toggle">
              {[["simple", "Simple / Estimate (No GST)"], ["gst", "Tax Invoice (With GST)"]].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setBuyForm({ ...buyForm, invoice_type: k })} data-testid={`invoice-type-${k}`}
                  className={`flex-1 text-xs font-bold px-3 py-2.5 rounded-md border ${buyForm.invoice_type === k ? "bg-[#0A3F2F] text-white border-[#0A3F2F]" : "bg-white border-[#E5E7E5] text-[#5C6A63]"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="purchase-supplier-select" className={inputCls} value={buyForm.supplier_id} onChange={(e) => pickBuySupplier(e.target.value)}>
                <option value="">Select supplier *</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select data-testid="purchase-add-product-select" className={inputCls} defaultValue="" onChange={addBuyItem}>
                <option value="">+ Add a product…</option>
                {products.map((p) => <option key={p.id} value={p.id}>{`${p.name} (stock: ${p.stock})`}</option>)}
              </select>
            </div>
            {buyItems.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_56px_56px_72px_56px_80px_88px_76px_24px] gap-1.5 text-[10px] uppercase font-bold text-[#5C6A63] px-1">
                  <span>Product</span><span>Qty</span><span>Free</span><span>Rate ₹</span><span>Disc %</span><span>Batch</span><span>Expiry</span><span className="text-right">Total</span><span />
                </div>
                {buyItems.map((it) => (
                  <div key={it.uid} className="grid grid-cols-[1fr_56px_56px_72px_56px_80px_88px_76px_24px] gap-1.5 items-center text-sm bg-[#FAFAF8] rounded-md px-1 py-1.5" data-testid={`buy-item-${it.uid}`}>
                    <span className="font-medium truncate pl-1">{it.name}</span>
                    <input type="number" step="any" min="0" className="border border-[#E5E7E5] rounded-md px-1.5 py-1 text-center text-xs" value={it.qty} onChange={(e) => setBuyItem(it.uid, "qty", e.target.value)} />
                    <input type="number" step="any" min="0" className="border border-[#E5E7E5] rounded-md px-1.5 py-1 text-center text-xs" placeholder="0" value={it.free_qty} onChange={(e) => setBuyItem(it.uid, "free_qty", e.target.value)} />
                    <input type="number" step="any" min="0" className="border border-[#E5E7E5] rounded-md px-1.5 py-1 text-center text-xs" value={it.cost} onChange={(e) => setBuyItem(it.uid, "cost", e.target.value)} />
                    <input type="number" step="any" min="0" max="100" className="border border-[#E5E7E5] rounded-md px-1.5 py-1 text-center text-xs" placeholder="0" value={it.discount_percent} onChange={(e) => setBuyItem(it.uid, "discount_percent", e.target.value)} />
                    <input className="border border-[#E5E7E5] rounded-md px-1.5 py-1 text-xs" placeholder="Batch" value={it.batch_no} onChange={(e) => setBuyItem(it.uid, "batch_no", e.target.value)} />
                    <input type="month" className="border border-[#E5E7E5] rounded-md px-1 py-1 text-[10px]" value={it.expiry} onChange={(e) => setBuyItem(it.uid, "expiry", e.target.value)} />
                    <span className="font-semibold text-right text-xs">{fmt(lineTotal(it))}</span>
                    <button type="button" onClick={() => setBuyItems(buyItems.filter((x) => x.uid !== it.uid))} className="text-gray-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {buyForm.invoice_type === "gst" && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">GST %</label>
                  <input data-testid="purchase-gst-input" type="number" step="any" min="0" className={inputCls} placeholder="e.g. 18" value={buyForm.gst_percent} onChange={(e) => setBuyForm({ ...buyForm, gst_percent: e.target.value })} />
                </div>
              )}
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Last Balance (L.B.) — previous dues ₹</label>
                <input data-testid="purchase-lb-input" type="number" step="any" min="0" className={inputCls} placeholder="0" value={buyForm.last_balance} onChange={(e) => setBuyForm({ ...buyForm, last_balance: e.target.value })} />
                {buySupplier && (buySupplier.balance_type || "to_pay") === "advance" && (buySupplier.opening_balance || 0) > 0 && (
                  <p className="text-[11px] text-emerald-700 font-semibold mt-1" data-testid="purchase-advance-hint">Advance jama with {buySupplier.name}: {fmt(buySupplier.opening_balance)}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="purchase-paid-input" type="number" step="any" min="0" className={inputCls} placeholder="Amount paying now (₹)" value={buyForm.amount_paid} onChange={(e) => setBuyForm({ ...buyForm, amount_paid: e.target.value })} />
              <select data-testid="purchase-method-select" className={inputCls} value={buyForm.payment_method} onChange={(e) => setBuyForm({ ...buyForm, payment_method: e.target.value })}>
                <option>Cash</option><option>UPI</option><option>Bank Transfer</option><option>Credit Card</option><option>Cheque</option>
              </select>
            </div>
            {buyForm.payment_method === "Credit Card" && (
              <select data-testid="purchase-card-select" className={inputCls} value={buyForm.credit_card_id} onChange={(e) => setBuyForm({ ...buyForm, credit_card_id: e.target.value })}>
                <option value="">Charge to card… (optional)</option>
                {cardsList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <div className="bg-[#0A3F2F] text-white rounded-md p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-white/70">Slip subtotal</span><span data-testid="purchase-subtotal">{fmt(buySubtotal)}</span></div>
              {buyForm.invoice_type === "gst" && <div className="flex justify-between"><span className="text-white/70">GST</span><span data-testid="purchase-gst-amt">{fmt(buyGst)}</span></div>}
              <div className="flex justify-between"><span className="text-white/70">Slip total</span><span data-testid="purchase-total">{fmt(buyTotal)}</span></div>
              {buyLB > 0 && <div className="flex justify-between"><span className="text-white/70">+ Last Balance (L.B.)</span><span data-testid="purchase-lb-amt">{fmt(buyLB)}</span></div>}
              <div className="flex justify-between font-bold border-t border-white/20 pt-1"><span>Total net outstanding</span><span data-testid="purchase-outstanding">{fmt(buyOutstanding)}</span></div>
              <div className="flex justify-between font-bold"><span>Balance to supplier after paying</span><span className="text-[#C5A059]" data-testid="purchase-pending">{fmt(buyPending)}</span></div>
            </div>
            <button type="submit" data-testid="purchase-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Record Purchase
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={uOpen} onOpenChange={setUOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{uEdit ? `Edit Usage — ${uEdit.product_name}` : "Record Salon Use"}</DialogTitle></DialogHeader>
          <form onSubmit={saveUsage} className="space-y-3" data-testid="usage-form">
            {uEdit ? (
              <div className="bg-[#FAFAF8] rounded-md px-3 py-2.5 text-sm font-semibold" data-testid="usage-edit-product">{uEdit.product_name}</div>
            ) : uProduct ? (
              <div className="flex items-center justify-between gap-2 bg-[#0A3F2F]/5 border border-[#0A3F2F]/20 rounded-md px-3 py-2.5" data-testid="usage-selected-product">
                <span className="text-sm font-semibold text-[#0A3F2F]">{uProduct.name}{uProduct.brand ? ` · ${uProduct.brand}` : ""}</span>
                <span className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${uProduct.stock <= 0 ? "bg-red-100 text-red-600" : uProduct.stock <= uProduct.low_stock_level ? "bg-[#C5A059]/20 text-[#8a6d33]" : "bg-emerald-600/10 text-emerald-700"}`} data-testid="usage-stock-badge">
                    Available Stock: {uProduct.stock} {uProduct.unit}
                  </span>
                  <button type="button" onClick={() => setUForm({ ...uForm, product_id: "" })} data-testid="usage-clear-product" className="text-gray-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                </span>
              </div>
            ) : (
              <div className="relative">
                <input data-testid="usage-product-search-input" className={inputCls} placeholder="🔍 Search product by name or brand… *" autoFocus
                  value={uQuery} onChange={(e) => { setUQuery(e.target.value); setUIdx(0); }} onKeyDown={usageKeyNav} />
                {uQuery.trim() && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7E5] rounded-md shadow-lg max-h-44 overflow-y-auto" data-testid="usage-product-suggestions">
                    {uMatches.length === 0 && <p className="px-3 py-2 text-xs text-[#5C6A63]">No matching products.</p>}
                    {uMatches.map((p, pi) => (
                      <button key={p.id} id={`usage-opt-${pi}`} type="button" onClick={() => pickUsageProduct(p)} data-testid={`usage-product-suggestion-${p.id}`}
                        disabled={(p.stock ?? 0) <= 0}
                        className={`w-full text-left px-3 py-2 text-sm border-b border-[#E5E7E5] last:border-0 flex items-center justify-between gap-2 ${(p.stock ?? 0) <= 0 ? "opacity-40 cursor-not-allowed" : pi === uIdx ? "bg-[#C5A059]/15" : "hover:bg-[#FAFAF8]"}`}>
                        <span>{p.name}{p.brand ? <span className="text-[#5C6A63]"> · {p.brand}</span> : null}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${(p.stock ?? 0) <= 0 ? "bg-red-100 text-red-600" : "bg-emerald-600/10 text-emerald-700"}`}>
                          {(p.stock ?? 0) <= 0 ? "OUT" : `${p.stock} ${p.unit}`}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="usage-qty-input" type="number" step="any" min="0" className={inputCls}
                placeholder={uForm.usage_unit === "g" ? "Quantity in g / ml *" : "Quantity *"} value={uForm.qty} onChange={(e) => setUForm({ ...uForm, qty: e.target.value })} />
              <select data-testid="usage-unit-select" className={inputCls} value={uForm.usage_unit} onChange={(e) => setUForm({ ...uForm, usage_unit: e.target.value })}>
                <option value="pcs">Pcs / Units</option>
                <option value="g">Grams (g) / ML</option>
              </select>
            </div>
            {uForm.usage_unit === "g" && (
              <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-3 space-y-1.5" data-testid="usage-pack-section">
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Container size — g/ml per {(uProduct || uEdit)?.unit || "pc"} *</label>
                <input data-testid="usage-pack-input" type="number" step="any" min="0" className={inputCls} placeholder="e.g. 60 (per colour tube)"
                  value={uForm.pack_size} onChange={(e) => setUForm({ ...uForm, pack_size: e.target.value })} />
                {(uProduct || uEdit) && Number(uForm.qty) > 0 && Number(uForm.pack_size) > 0 && (
                  <p className="text-[11px] font-semibold text-[#8a6d33]" data-testid="usage-deduct-preview">
                    Will deduct {(Number(uForm.qty) / Number(uForm.pack_size)).toFixed(2)} {(uProduct || uEdit)?.unit || "pcs"} from stock
                    {(uProduct?.purchase_price || uEdit?.unit_cost) ? ` · cost ${fmt((Number(uForm.qty) / Number(uForm.pack_size)) * (uProduct?.purchase_price ?? uEdit?.unit_cost ?? 0))}` : ""}
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="usage-date-input" type="date" className={inputCls} value={uForm.date} onChange={(e) => setUForm({ ...uForm, date: e.target.value })} />
              <select data-testid="usage-staff-select" className={inputCls} value={uForm.staff_id} onChange={(e) => setUForm({ ...uForm, staff_id: e.target.value })}>
                <option value="">Used by (optional)</option>
                {staffList.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <input data-testid="usage-note-input" className={inputCls} placeholder="Note (e.g. colour for Priya's global service)" value={uForm.note} onChange={(e) => setUForm({ ...uForm, note: e.target.value })} />
            <button type="submit" data-testid="usage-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              {uEdit ? "Save Changes" : "Log Usage"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!paying} onOpenChange={(v) => !v && setPaying(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Pay Supplier — {paying?.supplier_name}</DialogTitle></DialogHeader>
          <form onSubmit={payPurchase} className="space-y-4" data-testid="pay-supplier-form">
            <div className="bg-[#FAFAF8] rounded-md p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#5C6A63]">Total (incl. L.B.)</span><span>{fmt(paying?.grand_total ?? paying?.total)}</span></div>
              <div className="flex justify-between"><span className="text-[#5C6A63]">Already paid</span><span>{fmt(paying?.amount_paid)}</span></div>
              <div className="flex justify-between font-bold text-red-600"><span>Balance owed</span><span>{fmt(paying?.balance_due)}</span></div>
            </div>
            <input data-testid="pay-amount-input" type="number" step="any" min="1" className={inputCls} placeholder="Amount (₹)" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
            <select data-testid="pay-method-select" className={inputCls} value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}>
              <option>Cash</option><option>UPI</option><option>Bank Transfer</option><option>Credit Card</option><option>Cheque</option>
            </select>
            {payForm.method === "Credit Card" && (
              <select data-testid="pay-card-select" className={inputCls} value={payForm.credit_card_id} onChange={(e) => setPayForm({ ...payForm, credit_card_id: e.target.value })}>
                <option value="">Charge to card… (optional)</option>
                {cardsList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <button type="submit" data-testid="pay-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Record Payment
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
